import { Button } from "@mui/material";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  FACEBOOK_APP_ID,
  FACEBOOK_GRAPH_API_VERSION,
} from "../../utils/constants";

const CALLBACK_STATE_STORAGE_KEY = "kt_facebook_login_state";
/** Single-use: the stored state is cleared once matched, so a stale callback can't clear a pending login */
function consumeCallbackState(state: string | null) {
  if (!state || state !== sessionStorage.getItem(CALLBACK_STATE_STORAGE_KEY)) {
    return false;
  }
  sessionStorage.removeItem(CALLBACK_STATE_STORAGE_KEY);
  return true;
}
const FACEBOOK_REDIRECT_URL_PATHS =
  process.env.REACT_APP_FACEBOOK_REDIRECT_URL_PATHS?.split(";") || [
    "/settings/meta",
  ];
/** Get a whitelisted redirect Url for the facebook login, matching current path if possible */
function getFacebookRedirectUrl() {
  const urlPath = window.location.pathname;
  const whitelistedPath =
    FACEBOOK_REDIRECT_URL_PATHS.find((path) => urlPath.startsWith(path)) ??
    FACEBOOK_REDIRECT_URL_PATHS[0];
  return `${window.location.origin}${whitelistedPath}`;
}

// TODO: maybe do this with code instead, and check it on the BE
export type FacebookSuccessResponse = {
  /** An access token for the person using the webpage. */
  accessToken: string;
};

export type FacebookFailResponse = {
  error: string;
  errorReason: string | null;
  errorDescription: string | null;
};

export type FacebookLoginProps = {
  onSuccess?: (response: FacebookSuccessResponse) => void;
  onFail?: (error: FacebookFailResponse) => void;
  isLoading?: boolean;
  buttonText?: string;
};

const FacebookLogin = ({
  onSuccess,
  onFail,
  isLoading,
  buttonText = "Connect Facebook Account",
}: FacebookLoginProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const didHandleCallback = useRef(false);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (didHandleCallback.current) {
      // avoid calling callbacks multiple times
      return;
    }
    didHandleCallback.current = true;

    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has("error")) {
      if (consumeCallbackState(searchParams.get("state"))) {
        onFail?.({
          error: searchParams.get("error")!,
          errorReason: searchParams.get("error_reason"),
          errorDescription: searchParams.get("error_description"),
        });
      }
      return;
    }

    if (!!location.hash.length) {
      const search = new URLSearchParams(location.hash.substring(1));
      const accessToken = search.get("access_token");
      if (accessToken) {
        navigate("#");
        if (consumeCallbackState(search.get("state"))) {
          onSuccess?.({ accessToken });
        }
      }
    }
  }, [didHandleCallback]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFacebookLogin = () => {
    const state = crypto.randomUUID();
    sessionStorage.setItem(CALLBACK_STATE_STORAGE_KEY, state);
    const oauthUrl =
      `https://www.facebook.com/${FACEBOOK_GRAPH_API_VERSION}/dialog/oauth?client_id=${FACEBOOK_APP_ID}` +
      `&redirect_uri=${getFacebookRedirectUrl()}` +
      `&auth_type=rerequest` +
      `&scope=instagram_basic,pages_show_list,pages_read_engagement,business_management,instagram_manage_insights` +
      `&state=${state}` +
      `&response_type=token`;
    window.location.href = oauthUrl;
  };

  return (
    <Button
      sx={{ margin: "16px", width: "fit-content" }}
      variant="contained"
      size="medium"
      disabled={isLoading}
      onClick={handleFacebookLogin}
    >
      {buttonText}
    </Button>
  );
};

export default FacebookLogin;
